// TelcoTrack — ROZVADEC map marker model (circle + horizontal milestone halves).

import {
  type RozvadecMilestoneId,
  ROZVADEC_MILESTONE_IDS,
  type RozvadecMilestonesFlags,
  hasPendingRozvadecWeldingMilestones,
  isRozvadecMilestoneComplete,
} from './rozvadec-milestone-engine';

/** Map marker canvas (web SVG viewBox / Android bitmap). Matches web ROZVADEC circle size. */
export const ROZVADEC_MAP_MARKER_VIEWBOX_SIZE = 24;
export const ROZVADEC_MAP_MARKER_CIRCLE_INSET = 2;
/** Visible gap between top/bottom milestone halves (viewBox px). */
export const ROZVADEC_MAP_MARKER_HALF_GAP_PX = 2;

/** Halves top → bottom (same order as popup milestone buttons). */
export const ROZVADEC_MAP_HALF_MILESTONES: readonly RozvadecMilestoneId[] = ROZVADEC_MILESTONE_IDS;

export interface RozvadecMapHalfRect {
  y: number;
  height: number;
}

export type RozvadecMapMarkerKind = 'solid' | 'halved';

export interface RozvadecMapMarkerHalf {
  milestoneId: RozvadecMilestoneId;
  color: string;
  complete: boolean;
}

export interface RozvadecMapMarkerModel {
  kind: RozvadecMapMarkerKind;
  solidColor: string;
  halves: readonly RozvadecMapMarkerHalf[];
}

/** Top and bottom band positions inside the circle (inset applied). */
export function computeRozvadecMapHalfRects(): readonly [RozvadecMapHalfRect, RozvadecMapHalfRect] {
  const circleSize = ROZVADEC_MAP_MARKER_VIEWBOX_SIZE - ROZVADEC_MAP_MARKER_CIRCLE_INSET * 2;
  const gap = ROZVADEC_MAP_MARKER_HALF_GAP_PX;
  const halfHeight = (circleSize - gap) / 2;
  const y = ROZVADEC_MAP_MARKER_CIRCLE_INSET;
  return [
    { y, height: halfHeight },
    { y: y + halfHeight + gap, height: halfHeight },
  ];
}

export function shouldUseRozvadecHalvedMapMarker(
  _status: string | null | undefined,
  _flags?: RozvadecMilestonesFlags | null,
): boolean {
  // One welding phase — no primary/secondary halves.
  return false;
}

export function resolveRozvadecMilestoneHalfColor(
  complete: boolean,
  hostPhaseColor: string,
  nextPhaseColor: string,
): string {
  if (!complete) return hostPhaseColor;
  // Unlike BUDOVA stripes, ROZVADEC completed halves use the Provařené color as-is.
  // On Montáž host (Zafouknutý) and next (Provařené) are both green — no orange fallback.
  return nextPhaseColor;
}

export function resolveRozvadecMapMarkerModel(
  status: string | null | undefined,
  flags: RozvadecMilestonesFlags | null | undefined,
  options: {
    hostPhaseColor: string;
    nextPhaseColor: string;
    aggregatePhaseColor: string;
  },
): RozvadecMapMarkerModel {
  if (!shouldUseRozvadecHalvedMapMarker(status, flags)) {
    return {
      kind: 'solid',
      solidColor: options.aggregatePhaseColor,
      halves: [],
    };
  }

  const halves = ROZVADEC_MAP_HALF_MILESTONES.map((milestoneId) => {
    const complete = isRozvadecMilestoneComplete(milestoneId, flags);
    return {
      milestoneId,
      complete,
      color: resolveRozvadecMilestoneHalfColor(
        complete,
        options.hostPhaseColor,
        options.nextPhaseColor,
      ),
    };
  });

  return {
    kind: 'halved',
    solidColor: options.aggregatePhaseColor,
    halves,
  };
}

/** Stable cache key for Android/web/iOS bitmap/icon registration. */
export function buildRozvadecMapMarkerIconId(model: RozvadecMapMarkerModel): string {
  if (model.kind !== 'halved' || !model.halves.length) {
    return `rozvadec-solid-${model.solidColor.replace('#', '')}`;
  }
  const parts = model.halves.map((h) => h.color.replace('#', ''));
  return `rozvadec-${parts.join('-')}`;
}
